/**
 * Real-mode helpers for `LoraWizard.tsx`.
 *
 * These functions translate backend shapes (Phase 02 LoRA + training-run
 * envelopes from `api-alpha`) into the wizard's slide-friendly view-model.
 * They live in their own module so the wizard component stays a thin
 * dispatcher.
 */

import type {
  Lora,
  LoraBaseModel,
  TrainingRunStatusSlug,
  TrainingRunWithCheckpoints,
} from "@/types/loras";
import type {
  EpochSnapshot,
  EpochStatus,
  TrainingProgress,
  TrainingSettings,
  TrainingSummary,
} from "./types";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isLoraIdReal(value: string | undefined): value is string {
  return typeof value === "string" && UUID_RE.test(value);
}

/**
 * Active = the run is in some non-terminal status. Same set as the
 * orchestrator-side `NON_TERMINAL_TRAINING_STATUSES` constant.
 */
export function isTrainingRunActive(
  status: TrainingRunStatusSlug | undefined,
): boolean {
  if (!status) return false;
  return (
    status === "queued" ||
    status === "submitting" ||
    status === "pending_capacity" ||
    status === "training" ||
    status === "uploading_artifacts"
  );
}

/**
 * Shape sent to `POST /v1/loras/:id/training-runs` as `body.params`.
 * Mirrors the shape consumed by Phase 04's submit-lora-training handler
 * via `train_config.yaml` interpolation. The orchestrator currently
 * hardcodes a fallback template, so missing keys are non-fatal — keep
 * the payload minimal and let the trainer-side defaults apply.
 */
export interface SubmitTrainingParams {
  training: {
    rank: number;
    epochs: number;
    learningRate: number;
    snapshotEvery: number | "final";
    /** Trainer step count — forwarded so the orchestrator uses the base
     *  model's configured value instead of its 5000-step fallback. */
    steps?: number;
    /** Checkpoint cadence in steps (maps to ai-toolkit `save_every`). */
    saveEvery?: number;
  };
  augmentation: TrainingSettings["augmentation"];
  baseModelSlug: string;
  baseModelVariant?: string;
  referenceCount: number;
}

export function buildSubmitTrainingParams(args: {
  settings: TrainingSettings;
  baseModel: LoraBaseModel;
  referenceCount: number;
}): SubmitTrainingParams {
  const { settings, baseModel, referenceCount } = args;
  const config = baseModel.defaultTrainingConfig;
  const params: SubmitTrainingParams = {
    training: {
      rank: settings.rank,
      epochs: settings.epochs,
      learningRate: settings.learningRate,
      snapshotEvery: settings.snapshotEvery,
    },
    augmentation: settings.augmentation,
    baseModelSlug: baseModel.slug,
    referenceCount,
  };

  // Forward the base model's configured trainer step count + checkpoint
  // cadence. Without this the orchestrator's `renderTrainConfig` falls back to
  // 5000 steps / save_every 200 regardless of the model config, so every
  // wizard-submitted run trained for ~5–8h. (config keys are snake_case.)
  if (config && typeof config.steps === "number") {
    params.training.steps = config.steps;
  }
  if (config && typeof config.save_every === "number") {
    params.training.saveEvery = config.save_every;
  }

  // Phase 03 trainer reads `BASE_MODEL_VARIANT` from env; orchestrator
  // forwards `baseModelVariant` from params. We don't surface SRPO from
  // the UI in v1 — base-model slug is the dial.
  if (config && typeof config.baseModelVariant === "string") {
    params.baseModelVariant = config.baseModelVariant;
  }

  return params;
}

const TRAINING_RUN_STATUS_TO_LABEL: Record<string, string> = {
  queued: "Queued",
  submitting: "Submitting to AWS Batch",
  pending_capacity: "Waiting for GPU capacity",
  training: "Training",
  uploading_artifacts: "Uploading final artifacts",
  completed: "Complete",
  failed: "Failed",
  failed_resumable: "Failed (auto-resuming...)",
  resumed: "Resumed",
  cancelled: "Cancelled",
};

export function trainingRunStatusLabel(
  status: TrainingRunStatusSlug | undefined,
): string {
  if (!status) return "Preparing";
  return TRAINING_RUN_STATUS_TO_LABEL[status] ?? status;
}

/**
 * Map a backend `TrainingRunWithCheckpoints` into the slide's
 * `TrainingProgress` shape so `TrainingSlide` can render without
 * caring whether it's looking at the simulator or the real API.
 *
 * Notes:
 * - The simulator uses "epoch" as the unit of progress; the trainer
 *   uses "step". v1 surfaces step numbers directly (UI label adjusted
 *   below). Future polish: derive an epoch number from
 *   `step / steps_per_epoch` once that constant is in the training
 *   config.
 * - Sample-inference images are not generated by the v1 trainer, so
 *   the snapshot tiles render the asset id as a placeholder.
 */
export function deriveTrainingProgress(
  run: TrainingRunWithCheckpoints | undefined,
  settings: TrainingSettings,
): TrainingProgress | null {
  if (!run) return null;

  // Read the actual training step count from params.training.steps (set by
  // buildSubmitTrainingParams from the base model config). Falls back to
  // params.training.epochs for legacy runs, then to settings.epochs.
  const totalSteps =
    typeof run.params === "object" && run.params !== null
      ? (readNumber(run.params, ["training", "steps"]) ??
        readNumber(run.params, ["training", "epochs"]) ??
        settings.epochs)
      : settings.epochs;

  const snapshotInterval =
    settings.snapshotEvery === "final" ? totalSteps : settings.snapshotEvery;

  const checkpoints = [...(run.checkpoints ?? [])].sort(
    (a, b) => a.stepNumber - b.stepNumber,
  );

  const latestStep =
    checkpoints.length > 0
      ? checkpoints[checkpoints.length - 1].stepNumber
      : run.statusSlug === "completed"
        ? totalSteps
        : 0;

  // Synthesize a flat list of step statuses 1..totalSteps. The simulator
  // model relies on this for the progress bar; in real mode we only know
  // which checkpoints have been registered, not the precise step the
  // GPU is on right now. Fall back to "running" for steps not yet
  // checkpointed.
  const epochs: EpochStatus[] = Array.from({ length: totalSteps }, (_, i) => ({
    epoch: i + 1,
    status:
      i + 1 <= latestStep
        ? "complete"
        : i + 1 === latestStep + 1
          ? "running"
          : "pending",
  }));

  const snapshots: EpochSnapshot[] = checkpoints.map((cp) => ({
    epoch: cp.stepNumber,
    timestamp: new Date(cp.createdAt),
    loss: 0,
    previewImages: [],
    // Phase 4: surface the trainer's in-loop sample previews registered
    // onto this checkpoint, resolved to signed URLs at render time.
    sampleAssetIds: cp.sampleInferenceAssetIds ?? [],
  }));

  return {
    currentEpoch: latestStep,
    totalEpochs: totalSteps,
    timeRemaining: trainingRunStatusLabel(run.statusSlug),
    epochs,
    snapshots,
    snapshotInterval,
    startedAt: run.startedAt ? new Date(run.startedAt) : undefined,
  };
}

function readNumber(
  source: Record<string, unknown>,
  path: string[],
): number | undefined {
  let cursor: unknown = source;
  for (const key of path) {
    if (cursor && typeof cursor === "object" && key in (cursor as object)) {
      cursor = (cursor as Record<string, unknown>)[key];
    } else {
      return undefined;
    }
  }
  return typeof cursor === "number" ? cursor : undefined;
}

/**
 * LockSlide expects a `TrainingSummary` shape derived from the
 * simulator's local state. We assemble the equivalent from the backend
 * LoRA + last training run + base model. Anything we don't have from
 * the backend (likeness score, lighting conditions count, artifact
 * filename) falls back to neutral defaults.
 */
export function deriveTrainingSummary(args: {
  lora: Lora | undefined;
  trainingRun: TrainingRunWithCheckpoints | undefined;
  baseModel: LoraBaseModel | undefined;
  settings: TrainingSettings;
  castName: string;
}): TrainingSummary | null {
  const { lora, trainingRun, baseModel, settings, castName } = args;
  if (!lora || !trainingRun) return null;

  const trainedAt = trainingRun.completedAt
    ? new Date(trainingRun.completedAt)
    : new Date(trainingRun.createdAt);

  const gpuMinutes = trainingRun.computeSeconds
    ? Math.round(trainingRun.computeSeconds / 60)
    : 0;

  return {
    datasetSize: 0,
    lightingConditions: 0,
    triggerWord:
      lora.triggerWord ??
      `${(castName || lora.name || "lora").toLowerCase().replace(/\s+/g, "_").slice(0, 8)}_lora`,
    baseModel: baseModel?.slug ?? lora.baseModelSlug,
    baseModelLabel: baseModel?.displayName ?? lora.baseModelSlug,
    rank: settings.rank,
    epochs: settings.epochs,
    finalLoss: 0,
    likenessScore: 0,
    trainedAt,
    gpuMinutes,
    cost: trainingRun.costUsd ?? 0,
    artifactFilename: `${lora.id}.safetensors`,
    artifactSize: "—",
  };
}
